/* eslint-disable @typescript-eslint/no-explicit-any -- pre-existing legacy code, not touched in this pass; typing this properly is tracked separately */
import { CSSProperties } from "react";
import { RootState } from "store";
import { Entries, ScrollBehavior } from "utils/types";
import { applyOptionsUnitsToRequest } from "utils/units.utils";
import packageJson from "../../package.json";
import { url } from "./URL";

declare const window: Window;
declare const document: Document;
declare const sessionStorage: any;

export const isLocalhost = location.hostname === "localhost";
export const link = (str: string): string => (isLocalhost ? `http://localhost:4000/?path=${str}` : str);
export const img = (image: string): string =>
  isLocalhost ? `./design/images/${image}` : `/design/images/apps/user/dashboard/${image}`;
export const wait = (s: number) => new Promise((res) => setTimeout(res, s * 1000));
export const isNum = (num: any): num is Number => typeof num === "number";
export const isBoolean = (variable: any): variable is Boolean => typeof variable === "boolean";
export const isArray = (arr: any): arr is Array<any> => Array.isArray(arr);
export const isObject = (obj: any): obj is Object => typeof obj === "object";
export const isEmptyArray = <T extends any[]>(arr: T): boolean => !arr.length;
export const isEmptyObject = <T extends Object>(obj: T): boolean => !Object.entries(obj).length;
export const randomNum = (min: number, max: number) => Math.floor(Math.random() * (max - min)) + min;
export const hasArray = <T extends string>(arr: T[], el: T): boolean => arr.findIndex((item: T) => item === el) >= 0;
// eslint-disable-next-line no-console -- dev-only debug utility
export const separator = (width: number = 10): void => console.log(new Array(width).fill("=").join(""));
export const css = (el: any, styles: CSSProperties = {}) => Object.assign(el.style, styles);
export const isComparisonObjects = (obj: any, obj1: any): boolean => JSON.stringify(obj) === JSON.stringify(obj1);
export const stringify = (value: any): string => JSON.stringify(value);
export const logMutations = (obj: any): any => JSON.parse(JSON.stringify(obj));
export const isDefined = <T>(argument: T | undefined): argument is T => argument !== undefined;
export const entries = <T>(obj: T): Entries<T> => Object.entries(obj as any) as any;
export const flUp = (str: string): string => str.charAt(0).toUpperCase() + str.slice(1);
export const zero = (num: number): string => (num < 10 ? `0${num}` : num.toString());
export const include = (item: string, value: string): boolean =>
  item.toString().toLowerCase().includes(value.toLowerCase());
export const liveSearch = <T extends string>(inputValue: string, arr: T[]): T[] =>
  arr.filter((list: any) => include(list, inputValue));
export const scrollTop = (behavior?: ScrollBehavior) => window.scrollTo({ top: 0, behavior });
export const formatDate = (date: Date, isYear: boolean = true): string => {
  const [format, year] = date
    .toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
    .split(", ");
  return [format.split(" ").reverse().join(" ").toUpperCase(), isYear ? +year : ""].join(" ");
};
export const invariant = (message: string | string[]) => {
  throw new Error(typeof message === "string" ? message : message.join("\n"));
};
export const rgbToHex = (r: number, g: number, b: number): string =>
  "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);

const slice = <T>(array: T[], start: number, end: number): T[] => {
  let length: number = array == null ? 0 : array.length;
  if (!length) return [];
  start = start == null ? 0 : start;
  end = end === undefined ? length : end;

  if (start < 0) start = -start > length ? 0 : length + start;
  end = end > length ? length : end;
  if (end < 0) end += length;
  length = start > end ? 0 : (end - start) >>> 0;
  start >>>= 0;

  let index: number = -1;
  const result: T[] = new Array(length);
  while (++index < length) result[index] = array[index + start];
  return result;
};
export const chunk = <T>(array: T[], size: number = 1): T[][] => {
  size = Math.max(size, 0);
  const length: number = array == null ? 0 : array.length;
  if (!length || size < 1) return [];
  let index: number = 0;
  let resIndex: number = 0;
  const result: T[][] = new Array(Math.ceil(length / size)).fill([]);

  while (index < length) result[resIndex++] = slice<T>(array, index, (index += size));
  return result;
};

export const makeId = (length: number = 3): string => {
  let result: string = "";
  const characters: string = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) result += characters.charAt(Math.floor(Math.random() * charactersLength));
  return result;
};

export const IsJsonString = (str: string): boolean => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
};

/* eslint-disable no-console -- dev-only debug utility, logging is the point */
export const debugZone = (title: string, callback: () => void): void => {
  console.log("");
  console.log(`========== ${title} ===========`);
  callback();
  console.log(`========= /${title}/ ==========`);
  console.log("");
};
/* eslint-enable no-console */

export const debounce = <F extends (...args: any[]) => any>(func: F, waitFor: number) => {
  let timeout: ReturnType<typeof setTimeout> | null = null;

  const debounced = (...args: Parameters<F>) => {
    if (timeout !== null) {
      clearTimeout(timeout);
      timeout = null;
    }
    timeout = setTimeout(() => func(...args), waitFor);
  };

  return debounced as (...args: Parameters<F>) => ReturnType<F>;
};
export const parseJSON = <T>(value: string | null): T | undefined => {
  try {
    return value === "undefined" ? undefined : (JSON.parse(value ?? "") as T);
  } catch {
    // eslint-disable-next-line no-console -- surfaces JSON parse failures
    console.log("parsing error on", { value });
    return undefined;
  }
};

export const randIntException = (min: number, max: number, exp: number[]): number => {
  let n;
  exp = isArray(exp) ? exp : [isNaN(exp) ? min - 1 : exp];

  while (true) {
    n = Math.floor(Math.random() * (max - min + 1)) + min;
    if (exp.indexOf(n) < 0) return n;
  }
};

export const getDomain = (): string => {
  const currentSite = window.location.hostname;
  if (currentSite === "localhost") return "https://www.qubictron.loc";
  if (currentSite.includes(".qubictron.")) return `https://${currentSite}`;
  return "https://www.qubictron.com";
};
// export const domain = getDomain();
export const domain = url.baseURL;

export const hostNameWatcher = () => {
  const currentSite = window.location.hostname;
  return currentSite.includes("qubictron.");
};

export const createSourceDom = (): void => {
  if (hostNameWatcher()) return;

  const domain = getDomain();
  const hashBundle = packageJson.version.split(".").join("");

  // add script core.js
  const coreJS = document.createElement("script");
  coreJS.src = `${domain}/js/platform/core.js`;
  coreJS.async = true;
  document.body.appendChild(coreJS);

  const coreCSS = document.createElement("link");
  coreCSS.rel = "stylesheet";
  coreCSS.type = "text/css";
  coreCSS.media = "screen";
  coreCSS.href = `${domain}/design/css/apps/load-calculator/bundle.css.gz?${hashBundle}`;
  document.head.appendChild(coreCSS);
};

export const getReqBody = (data: RootState["root"]) => {
  const { response, data: dataField, autoCountContainers, hasUserToggledAutoCount, ...restData } = data;
  const cleanData = { ...restData };
  if (!!autoCountContainers.length) {
    cleanData.autoContainers = autoCountContainers;
  }
  return applyOptionsUnitsToRequest(cleanData);
};

export const asyncFunction = <T extends Object | null>(value: T, s: number = 0) =>
  new Promise(async (resolve) => {
    s ? await wait(s) : void 0;
    resolve(value);
  });
